import { Modal, ModalVariant } from '@hub/common/Modal/Modal';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import { useLifetimeParametersStore } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import { spatialTypeOfMovement } from '@hub/bearing-hub/common/constants';
import {
  ForceCode,
  getUnitLabelForUnitSystem,
  LengthCode,
  LinearFrequencyCode,
  TemperatureCode,
} from '@icommerce/units';
import { getDefaultTemperatureServiceLife } from './constants';
import type { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { UnitSystem } from '@igus/echain-shared-i18n';

export const OppositeMaterialMap = {
  '1': 'CF53',
  '2': 'X105_X90',
  '6': 'V2A',
  '8': 'ALU_HC',
  '9': 'CF53_CHROMED',
  '3': 'FREE_CUTTING_STEEL',
  '4': 'ST37',
} as const;

export type OppositeMaterialKeyType = keyof typeof OppositeMaterialMap;

interface LifeTimePopUpInfoProps {
  activeModal: boolean;
  setActiveModal: (value: boolean) => void;
  unitSystem: UnitSystem | IbhUnitSystem;
}

export const LifeTimePopUpInfo = ({ activeModal, setActiveModal, unitSystem }: LifeTimePopUpInfoProps) => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const router = useRouter();
  const locale = getLocale(router);
  const { lifetimeParameters } = useLifetimeParametersStore();

  const defaultLifeTimeData = [
    {
      key: t(`LIFETIME.OPPOSITE_MATERIAL`, { ns: 'bearing-hub/bearingHub' }),
      value: lifetimeParameters.oppositeMaterialId,
      unit: '',
    },
    {
      key: t(`LIFETIME.ENVIRONMENT`, { ns: 'bearing-hub/bearingHub' }),
      value: lifetimeParameters.environment,
      unit: '',
    },
    {
      key: t(`LIFETIME.LOAD`, { ns: 'bearing-hub/bearingHub' }),
      value: lifetimeParameters.force,
      unit: getUnitLabelForUnitSystem(ForceCode.NEWTON, unitSystem as UnitSystem),
    },
    {
      key: t(`LIFETIME.TEMPERATURE`, { ns: 'bearing-hub/bearingHub' }),
      value: getDefaultTemperatureServiceLife(unitSystem as IbhUnitSystem),
      unit: getUnitLabelForUnitSystem(
        TemperatureCode.CELSIUS,
        locale === 'en-US' ? UnitSystem.imperial : (unitSystem as UnitSystem)
      ),
    },
    {
      key: t(`LIFETIME.ACCEPTABLE_WEAR`, { ns: 'bearing-hub/bearingHub' }),
      value: lifetimeParameters.acceptableWear,
      unit: getUnitLabelForUnitSystem(
        LengthCode.MILLIMETER,
        locale === 'en-US' ? UnitSystem.imperial : (unitSystem as UnitSystem)
      ),
    },
    {
      key: t(`LIFETIME.ACTIVITY_MINUTES_PER_HOUR`, { ns: 'bearing-hub/bearingHub' }),
      value: lifetimeParameters.activityMinutesPerHour,
      unit: 'min',
    },
    {
      key: t(`LIFETIME.TYPE_OF_MOVEMENT`, { ns: 'bearing-hub/bearingHub' }),
      value: t(`LIFETIME.${lifetimeParameters.typeOfMovement.toUpperCase().replaceAll(' ', '_')}`, {
        ns: 'bearing-hub/bearingHub',
      }),
      unit: '',
    },
  ];

  const getLifetimeData = () => {
    if (
      lifetimeParameters.typeOfMovement === spatialTypeOfMovement.linear ||
      lifetimeParameters.typeOfMovement === spatialTypeOfMovement.linearAndPivoting
    ) {
      return [
        ...defaultLifeTimeData,
        {
          key: t(`LIFETIME.LINEAR_FREQUENCY`, { ns: 'bearing-hub/bearingHub' }),
          value: lifetimeParameters.linearFrequency,
          unit: getUnitLabelForUnitSystem(LinearFrequencyCode.REVOLUTION_PER_MINUTE, unitSystem as UnitSystem),
        },
        {
          key: t(`LIFETIME.LINEAR_STROKE_LENGTH`, { ns: 'bearing-hub/bearingHub' }),
          value: lifetimeParameters.linearStrokeLength,
          unit: getUnitLabelForUnitSystem(LengthCode.MILLIMETER, unitSystem as UnitSystem),
        },
      ];
    }

    if (lifetimeParameters.typeOfMovement === spatialTypeOfMovement.rotation) {
      return [
        ...defaultLifeTimeData,
        {
          key: t(`LIFETIME.ROTATIONAL_REVOLUTIONS`, { ns: 'bearing-hub/bearingHub' }),
          value: lifetimeParameters.rotationRevolutions,
          unit: 'rpm',
        },
      ];
    }

    if (lifetimeParameters.typeOfMovement === spatialTypeOfMovement.linearAndRotation) {
      return [
        ...defaultLifeTimeData,
        {
          key: t(`LIFETIME.LINEAR_FREQUENCY`, { ns: 'bearing-hub/bearingHub' }),
          value: lifetimeParameters.linearFrequency,
          unit: getUnitLabelForUnitSystem(LinearFrequencyCode.REVOLUTION_PER_MINUTE, unitSystem as UnitSystem),
        },
        {
          key: t(`LIFETIME.LINEAR_STROKE_LENGTH`, { ns: 'bearing-hub/bearingHub' }),
          value: lifetimeParameters.linearStrokeLength,
          unit: getUnitLabelForUnitSystem(LengthCode.MILLIMETER, unitSystem as UnitSystem),
        },
        {
          key: t(`LIFETIME.ROTATIONAL_REVOLUTIONS`, { ns: 'bearing-hub/bearingHub' }),
          value: lifetimeParameters.rotationRevolutions,
          unit: 'rpm',
        },
      ];
    }

    return [...defaultLifeTimeData];
  };

  return (
    <Modal
      variant={ModalVariant.DEFAULT}
      visible={activeModal}
      onClose={() => setActiveModal(false)}
      headline={t(`HEADLINES.LIFETIME`, { ns: 'bearing-hub/bearingHub' })}
    >
      <ul data-testid="life-time-modal-content">
        {getLifetimeData().map((item, index) => (
          <li key={item.key}>
            <span className="font-bold">{item.key}: </span>
            <span>
              {index === 0
                ? t(`SHAFT_MATERIAL.${OppositeMaterialMap[`${item.value}` as OppositeMaterialKeyType]}`, {
                    ns: 'bearing-hub/bearingHub',
                  })
                : item.value?.toLocaleString(locale)}{' '}
              {item.unit !== '' && item.unit}
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
};
